/**
 * Locale formatting for Paraguay. Centralised so a date or an amount is never formatted
 * ad hoc in a component — that is how the same value ends up rendered three ways on three
 * screens (CODE-006 applied to presentation).
 */

const LOCALE = 'es-PY';
const CURRENCY = 'PYG';

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const longDateFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * The guaraní has no minor unit in practice, so no decimals are shown. The thousands
 * separator is what actually makes an amount readable: 1.950.000 rather than 1950000.
 */
const currencyFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: CURRENCY,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat(LOCALE);

/** Accepts an ISO date (`2024-03-01`) or a full timestamp. */
export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? dateFormatter.format(date) : '—';
}

export function formatLongDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? longDateFormatter.format(date) : '—';
}

export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? dateTimeFormatter.format(date) : '—';
}

export function formatCurrency(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : currencyFormatter.format(value);
}

export function formatNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : numberFormatter.format(value);
}

/**
 * Parses an amount typed with es-PY thousands separators back into a number.
 * `1.950.000` reads as one million nine hundred fifty thousand here, not as 1.95.
 */
export function parseCurrencyInput(raw: string): number | null {
  const digits = raw.replace(/[^\d]/g, '');
  if (digits.length === 0) {
    return null;
  }
  return Number.parseInt(digits, 10);
}

/** Formats an amount for display inside a text input, as the user types. */
export function formatCurrencyInput(value: number | null): string {
  return value === null ? '' : numberFormatter.format(value);
}

export function formatInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  // A bare `YYYY-MM-DD` is parsed as UTC midnight by the Date constructor, which renders
  // as the previous day in a negative-offset timezone such as Paraguay's. Building it as
  // a local date avoids that off-by-one.
  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (isoDateOnly) {
    const [, year, month, day] = isoDateOnly;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
